import groupBy from 'lodash/groupBy';
import sumBy from 'lodash/sumBy';

import { InvestmentHolding } from '@compoundfinance/compound-core/dist/types/plaid';
import { ValueOf } from 'types';
import { TaxTreatment } from '../AllocationProposals/domain/InvestmentTarget';

const SubTypes = {
  CharitableGiving: 'charitable giving',
  IRA: 'ira',
  IRAAccounts: 'ira accounts',
  NonProfitGiving: 'non-profit giving',
  RetirementAccounts: 'retirement accounts',
  Brokerage: 'brokerage',
  BrokerageAccounts: 'brokerage accounts',
  ContributoryIRA: 'contributory ira',
  Education: 'education',
  Corporation: 'corporation',
  InvestmentAccounts: 'investment accounts',
  JointInvestment: 'joint investment',
  LimitedLiabilityCompany: 'limited liability company',
  LivingTrust: 'living trust',
  TenantsInCommon: 'tenants in common',
  TrustAccounts: 'trust accounts',
  Savings: 'savings',
  GeneralSavings: 'general savings',
  Other: 'other',
} as const;
type CompoundInvestSubTypes = ValueOf<typeof SubTypes>;

const SubTypeLabels = {
  [SubTypes.CharitableGiving]: 'Charitable giving',
  [SubTypes.IRA]: 'IRA',
  [SubTypes.IRAAccounts]: 'IRA Accounts',
  [SubTypes.NonProfitGiving]: 'Non-profit giving',
  [SubTypes.RetirementAccounts]: 'Retirement accounts',
  [SubTypes.Brokerage]: 'Brokerage',
  [SubTypes.BrokerageAccounts]: 'Brokerage accounts',
  [SubTypes.ContributoryIRA]: 'Contributory IRA',
  [SubTypes.Education]: 'Education',
  [SubTypes.Corporation]: 'Corporation',
  [SubTypes.InvestmentAccounts]: 'Investment accounts',
  [SubTypes.JointInvestment]: 'Joint investment',
  [SubTypes.LimitedLiabilityCompany]: 'Limited liability company',
  [SubTypes.LivingTrust]: 'Living trust',
  [SubTypes.TenantsInCommon]: 'Tenants in common',
  [SubTypes.TrustAccounts]: 'Trust accounts',
  [SubTypes.Savings]: 'Savings',
  [SubTypes.GeneralSavings]: 'General savings',
  [SubTypes.Other]: 'Other',
} as const satisfies Record<CompoundInvestSubTypes, string>;

const GroupSections = {
  RetirementAccounts: 'Retirement accounts',
  BrokerageAccounts: 'Brokerage accounts',
};

const SubTypeToGroupSection = {
  [SubTypes.RetirementAccounts]: GroupSections.RetirementAccounts,
  [SubTypes.ContributoryIRA]: GroupSections.RetirementAccounts,
  [SubTypes.IRA]: GroupSections.RetirementAccounts,
  [SubTypes.Brokerage]: GroupSections.BrokerageAccounts,
  [SubTypes.BrokerageAccounts]: GroupSections.BrokerageAccounts,
};

const SubTypeToTaxTreatment = {
  [SubTypes.CharitableGiving]: TaxTreatment.Retirement,
  [SubTypes.IRA]: TaxTreatment.Retirement,
  [SubTypes.IRAAccounts]: TaxTreatment.Retirement,
  [SubTypes.NonProfitGiving]: TaxTreatment.Retirement,
  [SubTypes.RetirementAccounts]: TaxTreatment.Retirement,
  [SubTypes.Brokerage]: TaxTreatment.Taxable,
  [SubTypes.BrokerageAccounts]: TaxTreatment.Taxable,
  [SubTypes.ContributoryIRA]: TaxTreatment.Taxable,
  [SubTypes.Education]: TaxTreatment.Taxable,
  [SubTypes.Corporation]: TaxTreatment.Taxable,
  [SubTypes.InvestmentAccounts]: TaxTreatment.Taxable,
  [SubTypes.JointInvestment]: TaxTreatment.Taxable,
  [SubTypes.LimitedLiabilityCompany]: TaxTreatment.Taxable,
  [SubTypes.LivingTrust]: TaxTreatment.Taxable,
  [SubTypes.TenantsInCommon]: TaxTreatment.Taxable,
  [SubTypes.TrustAccounts]: TaxTreatment.Taxable,
  [SubTypes.Savings]: TaxTreatment.Taxable,
  [SubTypes.GeneralSavings]: TaxTreatment.Taxable,
  [SubTypes.Other]: TaxTreatment.Taxable,
} as const satisfies Record<CompoundInvestSubTypes, string>;

const Constants = {
  SubTypes,
  SubTypeLabels,
  SubTypeToTaxTreatment,
  SubTypeToGroupSection,
};

function getCategoryValues(holdings: InvestmentHolding[]) {
  const holdingsByCategory = groupBy(
    holdings,
    (holding) => holding?.security?.assetCategory?.title ?? 'Others',
  );
  const categoryValues = Object.entries(holdingsByCategory).map(
    ([category, categoryHoldings]) => {
      const categoryValue = sumBy(categoryHoldings, 'institutionValue');
      const categoryReturn =
        categoryValue - sumBy(categoryHoldings, 'costBasis');
      return {
        category,
        value: categoryValue,
        totalReturn: categoryReturn,
        holdings: categoryHoldings,
      };
    },
  );

  return categoryValues;
}

const CompoundInvestUtils = {
  Constants,
  getCategoryValues,
};

export default CompoundInvestUtils;
